import { useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { withNamespaces } from 'react-i18next';
import {
    Card,
    CardBody,
} from 'reactstrap';

import useMailboxActions from '../../hooks/useMailboxActions';
import useMailboxBackPath from '../../hooks/useMailboxBackPath';
import useMailboxIntentPreview from '../../hooks/useMailboxIntentPreview';
import {
    getMailboxComposePath,
    buildMailboxReplyDraft,
    getMessageId,
    isUnreadMessage,
} from '../../helpers';
import MailboxBackButton from '../mailbox-back-button';
import MessageIntentAnalysisModal from '../message-intent-analysis-modal';
import MessageActions from './components/MessageActions';
import MessageAttachments from './components/MessageAttachments';
import MessageBody from './components/MessageBody';
import MessageHeader from './components/MessageHeader';
import MessageIntentActions from './components/MessageIntentActions';
import MessageLinkedCases from './components/MessageLinkedCases';
import MessageReply from './components/MessageReply';
import MessageSkeleton from './components/MessageSkeleton';
import useMailboxAttachments from './hooks/useMailboxAttachments';
import useMailboxIntents from './hooks/useMailboxIntents';
import useMailboxMessage from './hooks/useMailboxMessage';

const MessageDetail = ({
    t,
    messageId,
}) => {
    const history = useHistory();
    const location = useLocation();
    const {
        backFolder,
        backFolderTitleKey,
        goBack,
    } = useMailboxBackPath();
    const {
        message,
        isLoading,
        isFetching,
        isError,
    } = useMailboxMessage({
        t,
        messageId,
    });
    const {
        regularAttachments,
        rewrittenHtml,
        previewUrls,
        downloadAttachment,
        downloadingId,
    } = useMailboxAttachments({
        t,
        message,
    });
    const {
        intents,
        cases,
        isApplying,
        applyIntent,
    } = useMailboxIntents({
        t,
        messageId,
        message,
    });
    const preview = useMailboxIntentPreview({
        t,
        intents,
        applyCase: ({ intent, analysis }) => applyIntent(intent, analysis),
    });
    const {
        isActing,
        updateFlags,
        trashMessages,
        restoreMessages,
    } = useMailboxActions({
        t,
        onMoved: goBack,
    });
    const markedReadRef = useRef(null);

    useEffect(() => {
        markedReadRef.current = null;
    }, [messageId]);

    useEffect(() => {
        const id = getMessageId(message);

        if (!id || markedReadRef.current === id || !isUnreadMessage(message)) {
            return;
        }

        markedReadRef.current = id;
        updateFlags([id], { unread: false });
    }, [message, updateFlags]);

    if ((isLoading || isFetching) && !message) {
        return <MessageSkeleton />;
    }

    if (isError || !message) {
        return (
            <Card>
                <CardBody>
                    <MailboxBackButton
                        folderTitleKey={backFolderTitleKey}
                        onClick={goBack}
                    />
                    <p className="text-muted mb-0">
                        {t('This message could not be found.')}
                    </p>
                </CardBody>
            </Card>
        );
    }

    const id = getMessageId(message) || messageId;
    const actionsBusy = isActing || isApplying || preview.isBusy;
    const handleReply = () => {
        history.push({
            pathname: getMailboxComposePath(),
            state: {
                mailboxFolderPath: location.state?.mailboxFolderPath,
                mailboxComposeBackPath: location.pathname,
                mailboxReply: buildMailboxReplyDraft(message, {
                    folder: backFolder,
                }),
            },
        });
    };

    return (
        <Card>
            <CardBody className="mailbox-message-detail">
                <div className="mailbox-message-detail__toolbar">
                    <MailboxBackButton
                        folderTitleKey={backFolderTitleKey}
                        onClick={goBack}
                    />
                    <div className="d-flex align-items-center flex-wrap justify-content-end ms-auto mailbox-message-detail__toolbar-actions">
                        <MessageIntentActions
                            intents={intents}
                            cases={cases}
                        />
                        <MessageActions
                            message={message}
                            intents={intents}
                            cases={cases}
                            isActing={actionsBusy}
                            onTrash={() => trashMessages([id])}
                            onRestore={() => restoreMessages([id])}
                            onToggleStar={(starred) => updateFlags([id], { starred })}
                            onApplyIntent={(intent) => preview.startPreview({
                                messageId: id,
                                intent,
                                existingCases: cases,
                                message,
                            })}
                        />
                    </div>
                </div>
                <MessageLinkedCases cases={cases} />
                <MessageHeader message={message} />
                <MessageBody
                    message={message}
                    html={rewrittenHtml}
                />
                <MessageAttachments
                    attachments={regularAttachments}
                    previewUrls={previewUrls}
                    downloadingId={downloadingId}
                    onDownload={downloadAttachment}
                />
                <div className="mailbox-message-detail__footer">
                    <MessageReply
                        disabled={actionsBusy}
                        onReply={handleReply}
                    />
                </div>
            </CardBody>
            <MessageIntentAnalysisModal
                isOpen={preview.isOpen}
                session={preview.session}
                analysis={preview.analysis}
                isApplying={preview.isApplying}
                onCancel={preview.handleCancel}
                onConfirm={preview.handleConfirm}
            />
        </Card>
    );
};

export default withNamespaces()(MessageDetail);
